const CACHE_KEY = "boq.translationCache.v1";
const TEXT_NODE_ORIGINALS = new WeakMap();
const TEXT_NODE_APPLIED = new WeakMap(); // last value we wrote into a node
const ELEMENT_ATTR_ORIGINALS = new WeakMap();
const LETTER_PATTERN = /[A-Za-z\u10A0-\u10FF\u0400-\u04FF]/;

const BATCH_SIZE = 40;
const RETRY_AFTER_MS = 5 * 60 * 1000;
const failedUntil = new Map();

let translationCache = null;

const loadCache = () => {
  if (translationCache) return translationCache;

  try {
    translationCache = JSON.parse(window.localStorage.getItem(CACHE_KEY) || "{}");
  } catch (error) {
    translationCache = {};
  }

  return translationCache;
};

const saveCache = () => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(loadCache()));
  } catch (error) {
    // Ignore cache persistence failures.
  }
};

const shouldTranslate = (text) => {
  const trimmed = String(text || "").trim();
  if (!trimmed) return false;
  if (!LETTER_PATTERN.test(trimmed)) return false;
  if (/^[\d\s.,:%()+\-/*#]+$/.test(trimmed)) return false;
  return true;
};

const getElementAttrStore = (element) => {
  if (!ELEMENT_ATTR_ORIGINALS.has(element)) {
    ELEMENT_ATTR_ORIGINALS.set(element, {});
  }

  return ELEMENT_ATTR_ORIGINALS.get(element);
};

const collectTextEntries = (root) => {
  const entries = [];
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest("[data-no-translate='true']")) return NodeFilter.FILTER_REJECT;

        const tagName = parent.tagName;
        if (["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA"].includes(tagName)) {
          return NodeFilter.FILTER_REJECT;
        }

        // React may reuse this text node for new content; if its value is neither
        // our original nor our translation, it is a new original.
        if (
          TEXT_NODE_ORIGINALS.has(node) &&
          node.nodeValue !== TEXT_NODE_APPLIED.get(node) &&
          node.nodeValue !== TEXT_NODE_ORIGINALS.get(node)
        ) {
          TEXT_NODE_ORIGINALS.set(node, node.nodeValue || "");
        }
        const originalText = TEXT_NODE_ORIGINALS.get(node) ?? node.nodeValue ?? "";
        if (!shouldTranslate(originalText)) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  let currentNode = walker.nextNode();
  while (currentNode) {
    if (!TEXT_NODE_ORIGINALS.has(currentNode)) {
      TEXT_NODE_ORIGINALS.set(currentNode, currentNode.nodeValue || "");
    }

    entries.push({
      type: "text",
      node: currentNode,
      original: String(TEXT_NODE_ORIGINALS.get(currentNode) || ""),
    });
    currentNode = walker.nextNode();
  }

  return entries;
};

const collectAttributeEntries = (root) => {
  const entries = [];
  const elements = root.querySelectorAll("[placeholder], [title], [aria-label], input[type='button'], input[type='submit']");

  elements.forEach((element) => {
    if (element.closest("[data-no-translate='true']")) return;

    const attrStore = getElementAttrStore(element);
    ["placeholder", "title", "aria-label", "value"].forEach((attrName) => {
      if (attrName === "value" && element.tagName !== "INPUT") return;
      if (!element.hasAttribute(attrName)) return;

      if (!(attrName in attrStore)) {
        attrStore[attrName] = element.getAttribute(attrName) || "";
      }

      if (!shouldTranslate(attrStore[attrName])) return;

      entries.push({
        type: "attr",
        element,
        attrName,
        original: String(attrStore[attrName] || ""),
      });
    });
  });

  return entries;
};

const applyTranslations = (entries, translations) => {
  entries.forEach((entry) => {
    const translated = translations[entry.original.trim()] || entry.original;

    if (entry.type === "text") {
      entry.node.nodeValue = translated;
      TEXT_NODE_APPLIED.set(entry.node, translated);
      return;
    }

    entry.element.setAttribute(entry.attrName, translated);
  });
};

export function attachAutoTranslator({ root, language, translateBatch }) {
  if (!root || typeof translateBatch !== "function") {
    return () => {};
  }

  const cache = loadCache();
  if (!cache[language]) {
    cache[language] = {};
  }

  let disposed = false;
  let applying = false;
  let scheduled = false;

  const refresh = async () => {
    if (disposed || applying) return;

    const entries = [
      ...collectTextEntries(root),
      ...collectAttributeEntries(root),
    ];

    const now = Date.now();
    const missing = Array.from(
      new Set(
        entries
          .map((entry) => entry.original.trim())
          .filter(
            (text) =>
              text &&
              !cache[language][text] &&
              (failedUntil.get(`${language}|${text}`) || 0) < now
          )
      )
    );

    applying = true;
    try {
      // Small batches; strings the server could not translate (or a failed
      // request) are not retried for a while, so DOM changes can't hammer /translate.
      for (let i = 0; i < missing.length; i += BATCH_SIZE) {
        const chunk = missing.slice(i, i + BATCH_SIZE);
        let translated = {};
        try {
          translated = (await translateBatch(language, chunk)) || {};
        } catch (error) {
          console.warn("Automatic translation failed", error);
        }
        Object.assign(cache[language], translated);
        chunk.forEach((text) => {
          if (!translated[text]) failedUntil.set(`${language}|${text}`, now + RETRY_AFTER_MS);
        });
      }
      if (missing.length) saveCache();

      applyTranslations(entries, cache[language]);
    } catch (error) {
      console.warn("Automatic translation failed", error);
    } finally {
      applying = false;
    }
  };

  const scheduleRefresh = () => {
    if (scheduled || disposed) return;
    scheduled = true;
    window.requestAnimationFrame(async () => {
      scheduled = false;
      await refresh();
    });
  };

  const observer = new MutationObserver(() => {
    if (!applying) {
      scheduleRefresh();
    }
  });

  observer.observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["placeholder", "title", "aria-label", "value"],
  });

  scheduleRefresh();

  return () => {
    disposed = true;
    observer.disconnect();
  };
}